// Job Item editor and grid policy. SOR changes only its board layout.
import { inject } from '@angular/core';
import { ActivityEntityName, getJobSectionRights, getPortalContractorSectionRights, JobItemCommandId, JobItemSorBulkActionId, JobItemStatusBulkActionId, JobItemViewVariant, JobSecurityRight, MetadataEntityFieldData, StageItemDetail, jobManagePurchaseOrderRights, jobModifyAfterFinaliseRights, jobViewPricingRights, managePurchaseOrderRights, type WorkOrder } from '@jms/model';
import { CurrentUserService } from '@jms/ui/ui.standalone.services/current.user.service';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { PricingService } from '@jms/ui/ui.standalone.services/pricing.service';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { mergeEntityBoardConfig } from '@workbuddy/services/entities/merge.entity.board.config';
import type { EntityManifestBoardProfile } from '@workbuddy/services/entities/entity.manifest.surface';
import { embeddedItemBoardProfile, loadEmbeddedItemCatalogueFacts, type EmbeddedItemProfile } from '@workbuddy/surfaces/embedded-item';
import { Command } from '@workbuddy/ui/model';
import { createPaletteCommand } from '@workbuddy/ui/v2/blocks/command.palette';
import type { EntityBulkActionPickerPanel } from '@workbuddy/ui/v2/blocks/entity.bulk.actions';
import { EntityBoardDensityState, resolveBoardParams, type EntityBoardAddCommandData, type EntityBoardContext } from '@workbuddy/ui/v2/blocks/entity.board';
import { EntityDialogs } from '@workbuddy/ui/v2/blocks/entity.dialog';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { ItemCompletionCellRendererComponent } from '../../cell.renderers/item.completion';
import { ItemDescriptionCellRendererComponent, itemDescriptionCellValue } from '../../cell.renderers/item.description';
import { ItemIndicatorsCellRendererComponent, itemIndicatorsCellValue } from '../../cell.renderers/item.indicators';
import { ItemStatusCellRendererComponent, itemStatusCellValue } from '../../cell.renderers/item.status';
import { normalizeItemStageUpdatedAt, repriceItemField } from '../../support/item.edit.helpers';
import { buildJobItemRowCommands } from '../../commands/item.row.commands';
import { buildContractorItemRowCommands, isContractorItemRowLocked, resolveContractorItemRowScope } from '../../commands/contractor.item.row.commands';
import { injectWorkBuddyHost } from '@workbuddy/services/host/workbuddy.host.service';
import { CONTRACTOR_ITEM_COMMENT_BULK_ACTION } from '../../bulk.actions/contractor.item.comment.bulk.action';
import { CONTRACTOR_ITEM_COMPLETE_BULK_ACTION } from '../../bulk.actions/contractor.item.complete.bulk.action';
import { CONTRACTOR_ITEM_DELETE_BULK_ACTION } from '../../bulk.actions/contractor.item.delete.bulk.action';
import { CONTRACTOR_ITEM_EVIDENCE_BULK_ACTION } from '../../bulk.actions/contractor.item.evidence.bulk.action';
import { CONTRACTOR_ITEM_REQUEST_BULK_ACTION } from '../../bulk.actions/contractor.item.request.bulk.action';
import { CONTRACTOR_ITEM_RESOLVE_BULK_ACTION } from '../../bulk.actions/contractor.item.resolve.bulk.action';
import { ITEMS_CREATE_PURCHASE_ORDER_BULK_ACTION } from '../../bulk.actions/item.purchase.order.bulk.action';
import { groupSorBulkCommands } from '../../bulk.actions/item.sor.bulk.groups';
import { buildJobItemBrowserAdapter } from './job.item.browser.adapter';
import { shapeJobItemFilterFields } from './job.item.filter.fields';
import { resolveItemRecord } from '../../row/item.row.record';
import { ItemStatusSetService } from '../../services/item.status.set.service';
import { resolveActiveItemStatusPickerOptions } from '../../support/item.status.style';
import { AUTHORISATION_COLUMN_ID, authorisationGroupRank, isPendingItemRow, resolveAuthorisationGroup } from '../../row/job.item.authorisation.group';
import { resolveJobItemColumnOverrides, resolveJobItemSorColumnOverrides } from './job.item.columns';
import { jobItemSorExpandInteraction } from './open.job.item.surface.detail';
import { isItemPurchasable } from '../../support/item.purchasable';
import { isStageItemWritable } from '../../support/stage.item.writable';
import { stageItemTimezone } from '../../support/stage.item.timezone';
import { ItemSummaryStatusBarComponent } from '../../status.bar';
import { jobItemReadonly } from '../../../models/job.item.field.rules';
import type { JobItemBoardParams, JobItemBoardStage, JobItemRelationBoardParams } from '../../../models/job.item.model';
import { JobItemAddService } from '../../../services/job.item.add.service';
import { withPurchaseOrder } from '../../../surface/detail/job.item.detail.record';

const JOB_ITEM_SCOPE = 'workOrders.codes';

export function jobItemBoardProfile(base: EntityManifestBoardProfile, page: EntityBoardContext): Partial<EntityManifestBoardProfile> {

  // The contractor portal mounts this same board (WBS-11932, WBS-11933). Its rows answer from the
  // server-stamped capabilities rather than Jobs rights, its editor changes quantity, location and
  // instruction alone, and nothing here may reach a tenant route the portal does not proxy.
  const contractor = injectWorkBuddyHost().isContractorPortal;

  const editor = {
    entity: ActivityEntityName.JobItem,
    lineEntity: ActivityEntityName.EmbeddedItem,
    scope: JOB_ITEM_SCOPE,
    queryKey: [ActivityEntityName.JobItem, 'items'],
    // A subcontractor never moves a line's status; Request is their row action.
    fields: { longDescription: true, itemStatus: !contractor, payable: true },
    // The buy price is the subcontractor's own price, so the portal names it as its board does.
    labels: { note: 'Additional Instruction', ...(contractor ? { buyPrice: 'Unit Price' } : {}) },

    // One row editor for both boards: SOR and Items Pro are tabs on the same job, and two editors that
    // looked nothing alike taught the same job twice. The BOARD halves still differ — `sorOnly` below
    // drives columns, row height and cells, none of which the editor reads.
    layout: () => 'split',

    /**
     * Read whole rather than worked from the row: the board's projection is deliberately narrow, so
     * `note`, `codeInvoicingStatus`, `longDescription` and the custom-price flags are all missing from
     * it, and no row carries per-item capabilities. One GetItem on expand answers all of it.
     * Only an *approved* order freezes quantity, UoM and prices, and the item carries the link, not its
     * status — hence the purchase-order merge.
     */
    load: async (row, _params, injector) => {
      const item = resolveItemRecord(row);
      if (!item?._id) return null;

      const entityData = injector.get(EntityDataServiceRaw);

      // Scope only when the record is parent-shaped; a flat mount has no job / stage → undefined → scan.
      const parent = row && row !== item
        ? row as { _id?: string; workOrders?: { _id?: string } }
        : undefined;
      const detail = await firstValueFrom(entityData.executeCommand<StageItemDetail>(ActivityEntityName.JobItem, JobItemCommandId.GetItem, { itemId: item._id, jobId: parent?._id, stageId: parent?.workOrders?._id }));
      if (!detail) return null;

      const line = await withPurchaseOrder(detail, entityData);

      // Live catalogue, not the stored line: older lines omit canOverrideDesc and the editor would
      // otherwise offer Description, then the write refuses it (GCL01800). The UoM list is the
      // catalogue's too — a line stores only its own codeUoM, never the alternatives (WBS-11898).
      const catalogue = line.name
        ? await loadEmbeddedItemCatalogueFacts(line.name, injector)
        : { uoms: [], canOverrideDesc: false };

      // `ignoreOwnPriceForcing`: these gates are read once, at mount. The editor answers Billable and
      // Payable live, so the forcing they do to the price boxes is a model rule in the schema; a gate
      // holding the answer as loaded would keep a box locked after the user changed their mind.
      const gates = jobItemReadonly(line, { canOverrideDesc: catalogue.canOverrideDesc, ignoreOwnPriceForcing: true, contractor });

      return {
        line,
        uoms: catalogue.uoms,
        readonly: {
          location: gates.location,
          description: gates.description,
          longDescription: gates.longDescription,
          note: gates.note,
          quantity: gates.quantity,
          codeUoM: gates.codeUoM,
          buyPrice: gates.buyPrice,
          sellPrice: gates.sellPrice,
          codeInvoicingStatus: gates.codeInvoicingStatus,
          suppliedBySubcontractor: gates.suppliedBySubcontractor,
          payable: gates.payable,

          // Status never moves from the quick edit, whatever the rights (WBS-12286); a locked bind is never
          // dirty, so the patch carries no status either.
          itemStatus: true,

          // Job lines carry no Job rule: it is a planned-work decision, and this profile declares no bind for it.
          constructionRule: true,
        },
        canViewPricing: line.capabilities?.canViewPricing === true,

        // The sell side and the Billable / Payable / Supplied answers are the office's; a subcontractor keeps Unit Buy, their own price.
        ...(contractor ? { hide: { sellPrice: true }, hideFinancial: true } : {}),

        // Server-resolved against this item's job and stage; the band reads it to explain the lock.
        financeLock: line.financeLock,

        // Authorised offers Active Item statuses, Pending offers ItemPending. Inactive transitions are
        // row commands (cancel has a purchase-order gate and task side effects; Deleted is a soft marker).
        statusPicker: resolveActiveItemStatusPickerOptions(line.isClaimCode === true),
      };
    },

    priceReset: 'pricebook',
    // The portal has no pricebook route; the price on the line is the server's answer until it writes again.
    reprice: (field, line, codeUoM, injector) => contractor
      ? Promise.resolve(Number(line?.[field] ?? 0))
      : repriceItemField({ field, detail: line, codeUoM, isCustom: false, isReadonly: false, pricingService: injector.get(PricingService) }),

    // The UpdateItem command zeroes a forced price itself and refuses one in the patch.
    serverZeroesForcedPrices: true,

    save: async (line, patch, _params, injector) => {
      const updatedAt = normalizeItemStageUpdatedAt(line.stage?.updatedAt);
      if (!updatedAt) throw new Error('The current stage updatedAt is required. Refresh the Job and try again.');
      // Job and stage make the write's lookup the one indexed seek that covers authorised and pending
      // lines alike; by item id alone the server scans, and a portal Draft is not found (WBS-11958).
      await lastValueFrom(injector.get(EntityDataServiceRaw).executeCommand(ActivityEntityName.JobItem, JobItemCommandId.UpdateItem, { itemId: line._id, jobId: line.stage?.jobId, stageId: line.stage?._id, updatedAt, patch }));
    },

    // Compliance, defects, tasks, recap, header — outside the board's own query.
    afterWrite: params => params?.itemsChanged(),

    board: {
      shapeFilterFields: shapeJobItemFilterFields,
      header: {
        showViewOptions: { selector: 'pills' },

        // Shown only when hideAdd is false ($showAdd). Pending item reuses create with the pending seed.
        // A contractor's every line is a Draft claim line already, so the split would offer a distinction
        // the server does not make.
        addCommands: contractor
          ? []
          : [
            new Command({
              label: 'Add Pending Item',
              iconLeft: 'fa-regular fa-list-radio',
              data: { initialValues: { pending: true } } satisfies EntityBoardAddCommandData,
            }),
          ],
      },
      bulkActionHandlers: {

        // One status move per line kind and target type; each opens the status dialog, which collects the
        // reason a status may require (`item.status.bulk.action.ts`).
        [JobItemStatusBulkActionId.PendingChangeStatus]: () => import('../../bulk.actions/item.status.bulk.action').then(m => m.handleJobItemPendingChangeStatusBulkAction),
        [JobItemStatusBulkActionId.PendingCancel]: () => import('../../bulk.actions/item.status.bulk.action').then(m => m.handleJobItemPendingCancelBulkAction),
        [JobItemStatusBulkActionId.AuthorisedChangeStatus]: () => import('../../bulk.actions/item.status.bulk.action').then(m => m.handleJobItemAuthorisedChangeStatusBulkAction),
        [JobItemStatusBulkActionId.AuthorisedCancel]: () => import('../../bulk.actions/item.status.bulk.action').then(m => m.handleJobItemAuthorisedCancelBulkAction),

        // The SORs tab's Resolve and Decline pick their status in the same dialog; Request, Complete, Reopen and Delete confirm (WBS-12433).
        [JobItemSorBulkActionId.Resolve]: () => import('../../bulk.actions/item.sor.bulk.action').then(m => m.handleJobItemSorResolveBulkAction),
        [JobItemSorBulkActionId.Decline]: () => import('../../bulk.actions/item.sor.bulk.action').then(m => m.handleJobItemSorDeclineBulkAction),

        // Three upload buckets do not fit the palette's single-value panel, so the descriptor declares no
        // editor and this handler owns the composer dialog.
        'items.evidence.add': () => import('../../bulk.actions/item.evidence.bulk.action').then(m => m.handleJobItemEvidenceBulkAction),

        // Free prose wants more than the palette's one-line row, which also rendered without its label.
        'items.comment.add': () => import('../../bulk.actions/item.comment.bulk.action').then(m => m.handleJobItemCommentBulkAction),

        // The picker only stores the destination stage. The move is one pipeline call per source stage.
        'items.move.stage': () => import('../../bulk.actions/item.move.stage.bulk.action').then(m => m.handleJobItemMoveStageBulkAction),

        // One order from the whole selection, so the handler runs it itself and opens the Purchase Order surface.
        'items.purchase.order.create': () => import('../../bulk.actions/item.purchase.order.bulk.action').then(m => m.handleJobItemPurchaseOrderBulkAction),
      },

      // Attach File / Add Defects / Delete or Cancel by tenant setting: the whole menu is Job's, so the kit Delete steps aside.
      rowCommands: (row, params) => contractor ? buildContractorItemRowCommands(row, params) : buildJobItemRowCommands(row, params),

      // Resolved inside the board injection context so tenant visibility settings remain reactive.
      // The status set is warmed here because this runs once at mount: the row menu is built when it opens
      // and reads the set synchronously, so anything started there is always too late for the first open.
      // Stage Number is hidden on a stage's own Items tab (WBS-11907): every row there is that stage's.
      // The mount parent tells the level — a job-level tab passes the job id, a stage tab the stage id.
      // Not `stageId` (null when the stage is not writable) nor `stageCount` (Manage Job Pro reports the
      // job's count on every tab). The column stays on the shared view so the job-level tab keeps it.
      columns: context => {

        inject(ItemStatusSetService).load();

        const jobId = resolveBoardParams<JobItemBoardParams>(context)?.job?.()?._id;
        const parentId = context.header?.parentId;

        return resolveJobItemColumnOverrides({
          compact: inject(EntityBoardDensityState).$compact(),
          stageLevel: !!jobId && !!parentId && parentId !== jobId,
          contractor,
        });
      },
      statusBar: {
        component: ItemSummaryStatusBarComponent,
        align: 'right',
        height: 62,
      },

      // Both mounts carry an All tab grouped by authorisation. Group order is otherwise
      // whichever bucket happens to hold the lowest sortOrder — the group column is left unsortable
      // (buildColumnDefs) and applyColumnState clears sort on every column but sortOrder, so AG falls
      // back to the order rows arrive in. `initial` means a sort the user applies later still wins.
      overrideOptions: (_context, options) => ({
        ...options,
        initialGroupOrderComparator: (params: { nodeA?: { key?: string }; nodeB?: { key?: string } }) => authorisationGroupRank(params.nodeA?.key) - authorisationGroupRank(params.nodeB?.key),
      }),

      // The kit already zeroes currency without the pricing right and relabels the EXT headers; what is
      // left is the All tab's group value, on either mount. The group column is injected by the board (the
      // view groups by a field that is not one of its columns), so it carries no cellRendererParams.field —
      // match it by id. Rows here are parent-shaped, so the item always sits at workOrders.codes.
      overrideColumnDefs: (context, columnDefs) => {

        const extended = resolveBoardParams<JobItemBoardParams>(context)?.showAdditionalLineFields?.() === true;

        return columnDefs.map(columnDef => {
          if (columnDef?.colId === AUTHORISATION_COLUMN_ID) {
            return {
              ...columnDef,
              valueGetter: (cell: { data?: { workOrders?: { codes?: { authorisation?: string } } } }) =>
                resolveAuthorisationGroup(cell.data?.workOrders?.codes),
            };
          }

          // "Buy" is the office's word for what a subcontractor is paid; on the portal it is simply their price.
          const field = (columnDef?.cellRendererParams as { field?: MetadataEntityFieldData } | undefined)?.field;
          if (contractor && field?.name === 'buyPrice') {
            return { ...columnDef, headerName: extended ? 'EXT Price' : 'Unit Price' };
          }

          return columnDef;
        });
      },

      // The name / quantity / buy / sell cells are the kit's. Spread currentRenderer: board sets `type`
      // ('numericColumn') for header alignment.
      overrideCellRenderer: (_context, _metadata, field, currentRenderer) => {
        switch (field.name) {
          case 'status':
          case 'itemStatus':
            return {
              ...currentRenderer,
              cellRenderer: ItemStatusCellRendererComponent,
              cellRendererParams: {
                ...currentRenderer.cellRendererParams,
                sorMode: false,
                ...(contractor ? { hideBadges: true, isLocked: isContractorItemRowLocked } : {}),
              },
              valueGetter: itemStatusCellValue,
            };

          // SOR sets the same cell for everyone; the portal's Items board wants the note under the description too.
          case 'description':
            return contractor
              ? { ...currentRenderer, cellRenderer: ItemDescriptionCellRendererComponent, valueGetter: itemDescriptionCellValue }
              : currentRenderer;

          default:
            return currentRenderer;
        }
      },
    },
  } satisfies EmbeddedItemProfile<StageItemDetail, JobItemBoardParams>;

  const entityBoard = embeddedItemBoardProfile(editor);

  const supplied = page.params as JobItemBoardParams | JobItemRelationBoardParams | undefined;
  let params: JobItemBoardParams | undefined;

  if (supplied) {
    params = 'job' in supplied
      ? supplied
      : buildRelationBoardParams(supplied);
  }

  const jobId = params?.job?.()?._id ?? null;

  const sorOnly = params?.variant === JobItemViewVariant.SOR || page.header?.variant === JobItemViewVariant.SOR;
  const stageId = params?.stageId?.() ?? null;
  // Add, Import and the item template need the section's Create on top of a writable stage (Jobs Manage).
  const canCreateLines = !params?.sectionRights || !!params.sectionRights()?.create;
  const canAdd = !!stageId && !!jobId && stageId !== jobId && canCreateLines;
  // Export only reads what the tab already lists, so it needs the tab's View alone, on any stage (WBS-11929).
  const selectedStageId = page.header?.parentId ?? null;
  const canExport = !!params?.onExport && !contractor && !!jobId && !!selectedStageId && selectedStageId !== jobId;
  // Export Items joins the Import menu when Import is offered, and stands alone for a role that cannot import.
  const canImport = canAdd && !!params?.onImport && !contractor;
  const exportCommand = () => new Command({ label: 'Export Items', iconLeft: 'fa-regular fa-download', action: () => params!.onExport!() });
  const addService = inject(JobItemAddService);
  const dialogs = inject(EntityDialogs, { optional: true });
  const itemBrowser = params
    ? (initialValues?: Record<string, unknown>) => buildJobItemBrowserAdapter(
      { ...params, sorOnly: () => sorOnly },
      addService,
      initialValues,
    )
    : undefined;

  const onExpand = sorOnly
    ? jobItemSorExpandInteraction(dialogs, params)?.onExpand
    : undefined;

  const moveToStageCommands = (selectedItems: unknown[]): Command[] => {
    const sourceStages = selectedItems.map(row => (row as { workOrders?: { _id?: string } })?.workOrders?._id);
    if (!params || !sourceStages.length || sourceStages.some(id => !id || !params.stageWritable(id))) return [];
    // A move edits the line, so it needs the section's Edit (the server refuses it otherwise).
    if (params.sectionRights && !params.sectionRights()?.edit) return [];

    const selectedStages = new Set(sourceStages);
    const stages = params.stages().filter(stage => !selectedStages.has(stage.id));
    if (!stages.length || (!!stageId && !!jobId && stageId === jobId)) return [];

    const label = (stage: JobItemBoardStage) => {
      if (stage.name) return `${stage.name} - ${stage.number}`;
      return stage.number;
    };
    const settingsPanel: EntityBulkActionPickerPanel = {
      kind: 'picker',
      fieldId: 'items_move_stage',
      valueKey: 'stageId',
      options: stages.map(stage => ({ id: stage.id, label: label(stage) })),
      mode: 'single',
      value: '',
      selectedValue: value => {
        const stage = stages.find(candidate => candidate.id === value);
        if (!stage) return '';
        return label(stage);
      },
    };

    return [createPaletteCommand({
      id: 'items.move.stage',
      label: 'Move to stage',
      iconLeft: 'fa-regular fa-arrow-right-to-bracket',
      palette: { settingsPanel },
      data: {
        bulkActionDescriptor: {
          id: 'items.move.stage',
          label: 'Move to stage',
          kind: 'command',
          primary: true,
        },
      },
    })];
  };

  // Legacy's Items tab offered Create Purchase Order on the job-level tab alone (`!isWorkOrder`): an order
  // draws lines from across the job's stages, so a stage tab is the wrong place to start one (WBS-11890).
  const stageLevel = !!selectedStageId && selectedStageId !== jobId;
  const portalRights = contractor ? inject(RightsService) : null;

  /**
   * The portal SORs tab (WBS-12406). The host runs each one line at a time over the contractor routes. Add comment
   * needs SORs › View, as the SOR Details note does; evidence, Complete and Resolve write the SOR's task, so they need
   * Tasks › View + Edit; all four need the stage still open to the subcontractor. Complete and Resolve skip pending
   * lines, so a selection of pending lines only does not offer them. Delete stays on the row ⋮ only.
   */
  const contractorSorBulkCommands = (selectedItems: unknown[]): Command[] => {
    const sections = getPortalContractorSectionRights(portalRights?.role);
    const stageOpen = params?.stageOpenForContractor?.() === true;
    const canWriteTasks = stageOpen && sections.tasks.view && sections.tasks.edit;
    const canRequest = selectedItems.some(row => resolveContractorItemRowScope(row)?.capabilities?.canUpdate === true);
    const anyApproved = selectedItems.some(row => !isPendingItemRow(resolveItemRecord(row) as { authorisation?: string } | null));
    const favourite = (id: string, label: string, icon: string) => new Command({ _id: id, label, iconLeft: icon, data: { bulkActionDescriptor: { id, label, kind: 'command', primary: true } } });

    return [
      ...(stageOpen && sections.sors.view ? [favourite(CONTRACTOR_ITEM_COMMENT_BULK_ACTION, 'Add comment', 'fa-regular fa-message-lines')] : []),
      ...(canWriteTasks ? [favourite(CONTRACTOR_ITEM_EVIDENCE_BULK_ACTION, 'Add Photo / Video Evidence', 'fa-regular fa-image')] : []),
      ...(canRequest ? [favourite(CONTRACTOR_ITEM_REQUEST_BULK_ACTION, 'Request SORs', 'fa-regular fa-paper-plane')] : []),
      ...(canWriteTasks && anyApproved ? [new Command({ _id: CONTRACTOR_ITEM_COMPLETE_BULK_ACTION, label: 'Mark SOR as Complete', iconLeft: 'fa-regular fa-circle-check' })] : []),
      ...(canWriteTasks && anyApproved ? [new Command({ _id: CONTRACTOR_ITEM_RESOLVE_BULK_ACTION, label: 'Mark SOR as Resolved', iconLeft: 'fa-regular fa-circle-minus' })] : []),
    ];
  };

  const createPurchaseOrderCommands = (selectedItems: unknown[]): Command[] => {
    if (contractor || !jobId || stageLevel) return [];
    // Rows already ordered, billed or supplied by the subcontractor are left out of the request, not of the offer.
    if (!selectedItems.some(row => isItemPurchasable(resolveItemRecord(row)))) return [];
    // Legacy's `managePurchaseOrdersOnJobs`, plus the Finance right the Purchase Order create surface authorises on.
    const rights = inject(RightsService);
    if (!rights.hasRights(...jobManagePurchaseOrderRights) || !rights.hasRights(...managePurchaseOrderRights)) return [];

    return [new Command({
      _id: ITEMS_CREATE_PURCHASE_ORDER_BULK_ACTION,
      label: 'Create Purchase Order',
      iconLeft: 'fa-regular fa-cart-shopping',
      // The descriptor puts it on the bar beside Move; the manifest handler claims it before the bulk pipeline runs.
      data: { bulkActionDescriptor: { id: ITEMS_CREATE_PURCHASE_ORDER_BULK_ACTION, label: 'Create Purchase Order', kind: 'command', primary: true } },
    })];
  };

  const common: Partial<EntityManifestBoardProfile> = {
    // The pen only where the server says the line may still change; the office reads the row's
    // status gates inside the editor instead, so its pen stays on every row.
    ...(contractor && base.rowEditors?.inline ? { rowEditors: { inline: { ...base.rowEditors.inline, when: (row: unknown) => resolveContractorItemRowScope(row)?.capabilities?.canUpdate === true } } } : {}),
    header: {
      hideAdd: !canAdd,
      primaryCommands: [
        ...(canImport
          ? [
            {
              command: new Command({
                label: 'Import',
                iconLeft: 'fa-regular fa-upload',
                action: () => params.onImport!(stageId),
              }),
              commands: [
                new Command({ label: 'Template', iconLeft: 'fa-regular fa-download', action: () => params.onDownloadTemplate?.() }),
                ...(canExport ? [exportCommand()] : []),
              ],
            },
          ]
          : []),
        ...(canExport && !canImport ? [exportCommand()] : []),
      ],
    },
    itemInteraction: {
      // The portal opens SOR Details by Expand only; it declares no preview for the board's row-open fallback to show.
      ...(contractor ? { navigate: () => undefined } : {}),
      selectable: {
        type: 'multiple',
        bulkCommands: ({ selectedItems }) => {
          // The portal host runs these one line at a time: the bulk pipeline asserts a Jobs right a subcontractor never holds.
          if (contractor && sorOnly) return contractorSorBulkCommands(selectedItems);
          if (contractor) {
            const capabilities = selectedItems.map(row => resolveContractorItemRowScope(row)?.capabilities);
            return [
              ...(capabilities.some(capability => capability?.canUpdate === true) ? [new Command({ _id: CONTRACTOR_ITEM_REQUEST_BULK_ACTION, label: 'Request items', iconLeft: 'fa-regular fa-paper-plane' })] : []),
              ...(capabilities.some(capability => capability?.canDelete === true) ? [new Command({ _id: CONTRACTOR_ITEM_DELETE_BULK_ACTION, label: 'Delete items', iconLeft: 'fa-regular fa-trash' })] : []),
            ];
          }

          return [
            ...createPurchaseOrderCommands(selectedItems),
            ...moveToStageCommands(selectedItems),
          ];
        },
      },
    },
    params: {
      ...params,
      sorOnly: () => sorOnly,
      itemBrowser,
    },
  };

  if (!sorOnly) {
    // The note line under the description needs SOR's row height.
    const items: Partial<EntityManifestBoardProfile> = contractor
      ? { ...common, table: { overrideOptions: (context, options) => ({ ...(entityBoard.table?.overrideOptions?.(context, options) ?? options), ...tallRowOptions }) } }
      : common;

    return mergeEntityBoardConfig(entityBoard, items) as Partial<EntityManifestBoardProfile>;
  }

  return mergeEntityBoardConfig(entityBoard, {
    ...common,
    shapeFilterFields: (fields, view) => shapeJobItemFilterFields(fields, view, { sorOnly: true }),
    header: {
      ...common.header,
      title: 'SOR',
      variant: JobItemViewVariant.SOR,
    },
    // The office SORs tab's palette in the ticket's sections (WBS-12433); the portal keeps its own list.
    ...(contractor ? {} : { bulk: { groupCommands: groupSorBulkCommands } }),
    itemInteraction: {
      ...common.itemInteraction,
      ...(onExpand ? { onExpand } : {}),
    },
    table: {
      columns: () => {
        inject(ItemStatusSetService).load();
        return resolveJobItemSorColumnOverrides();
      },
      overrideOptions: (context, options) => ({
        ...(entityBoard.table?.overrideOptions?.(context, options) ?? options),
        ...tallRowOptions,
        autoSizeStrategy: {
          type: 'fitCellContents',
          colIds: [
            'workOrders.codes.quantity',
            'workOrders.codes.buyPrice',
            'workOrders.codes.sellPrice',
            'workOrders.codes.itemStatus',
          ],
        },
      }),
      overrideCellRenderer: (context, metadata, field, renderer) => {
        const shared = base.table?.overrideCellRenderer?.(context, metadata, field, renderer) ?? renderer;

        switch (field.name) {
          case 'status':
          case 'itemStatus':
            return {
              ...shared,
              cellRendererParams: { ...shared.cellRendererParams, sorMode: true },
            };

          case 'indicators':
            return {
              ...shared,
              cellRenderer: ItemIndicatorsCellRendererComponent,
              // The evidence hover reads Recorded on in the zone the line's detail page uses (WBS-12550).
              cellRendererParams: { ...shared.cellRendererParams, hideFinance: contractor, timezone: params?.timezone },
              valueGetter: itemIndicatorsCellValue,
            };

          case 'completion':
            return { ...shared, cellRenderer: ItemCompletionCellRendererComponent, cellRendererParams: { ...shared.cellRendererParams, timezone: params?.timezone } };

          case 'description':
            return {
              ...shared,
              cellRenderer: ItemDescriptionCellRendererComponent,
              valueGetter: itemDescriptionCellValue,
            };

          default:
            return shared;
        }
      },
    },
  }) as Partial<EntityManifestBoardProfile>;
}

const tallRowOptions = {
  rowHeight: 56,
  getRowHeight: (params: { node?: { group?: boolean } }) => {
    if (params.node?.group) return 40;
    return 56;
  },
};

function buildRelationBoardParams(source: JobItemRelationBoardParams): JobItemBoardParams {

  const users = inject(CurrentUserService);
  const settings = inject(GeneralSettingService);
  const rights = inject(RightsService);
  const writable = (stage: WorkOrder | null | undefined) =>
    !source.isReadonly()
    && !!stage
    && isStageItemWritable(
      stage,
      rights.hasRights(JobSecurityRight.Manage),
      rights.hasRights(...jobModifyAfterFinaliseRights),
    );
  const common = {
    variant: source.variant,
    currentUser: () => users.$user(),
    itemsChanged: source.itemsChanged,
    viewPricing: () => rights.hasRights(...jobViewPricingRights),
    showAdditionalLineFields: () => !!settings.generalSettings?.items?.displayAdditionalLineFields,
    // Same section rights Manage Job passes, so Add, row ⋮ and Move follow the tab's section instead of answering 403.
    sectionRights: () => {
      const sections = getJobSectionRights(rights.role);
      return source.variant === JobItemViewVariant.SOR ? sections.sors : sections.items;
    },
  };

  if (source.relation === 'job') {
    const job = source.record;

    return {
      ...common,
      job: () => ({ _id: job()._id, name: job().jobNumber }),
      stageCount: () => job().workOrders?.length ?? 0,
      stages: () => (job().workOrders ?? []).map(stage => ({
        id: stage._id,
        name: stage.name ?? '',
        number: stage.workOrderNumber,
      })),
      stageId: () => {
        const candidate = job().mode === 'Basic'
          ? job().workOrders?.[0]
          : null;
        return writable(candidate) ? candidate._id : null;
      },
      stageWritable: stageId => writable(job().workOrders?.find(stage => stage._id === stageId)),
      timezone: stageId => stageItemTimezone(job().workOrders?.find(stage => stage._id === stageId), job().site, settings.generalSettings?.region?.timezone),
    };
  }

  const stage = source.record;

  return {
    ...common,
    job: () => ({ _id: stage().jobId ?? '', name: stage().jobNumber ?? stage().name }),
    stageCount: () => 1,
    stages: () => [{ id: stage()._id, name: stage().name ?? '', number: stage().workOrderNumber }],
    stageId: () => writable(stage()) ? stage()._id : null,
    stageWritable: stageId => stageId === stage()._id && writable(stage()),
    // The stage read projects `site` as its effective site, so it stands in for the job site here.
    timezone: () => stageItemTimezone(stage(), stage().site, settings.generalSettings?.region?.timezone),
  };
}
